import React, { useCallback, useState } from 'react';
import { Button, CircularProgress, Grid, Typography } from '@material-ui/core';
import Dialog from '@material-ui/core/Dialog';
import DialogTitleWithCloseButton from '../DialogTitleWithCloseButton';
import { isEmpty } from 'lodash';
import Stepper from '@material-ui/core/Stepper';
import Step from '@material-ui/core/Step';
import StepLabel from '@material-ui/core/StepLabel';
import DialogContent from '@material-ui/core/DialogContent';
import Confirm from '../forked/layout/Confirm';
import DialogActions from '@material-ui/core/DialogActions';
import { useStyles } from '../../../features/users/creationsteps/StyleStep';

const WizardStepper = props => {
  const {
    hasRadioButtons,
    displayRadiosButtons,
    handleClose,
    getActiveStep,
    handleBackForm,
    handleNextForm,
    resource,
    resourceCreated,
    displayResourceCreatedInfo,
    shouldDisplayResourceCreatedInfo,
    getDescriptionContainerStyle,
    isBackDisabled,
    isNextAvailable,
    activeListOfSteps,
    activeStep,
    isSpinnerOnNextButton,
    isSpinnerOnSaveButton,
    isSaveAndCloseStep,
    providerFormContext,
    customContext,
    generateMessageOnLeave,
    isFirstStepPristine,
    shouldHideBackButton
  } = props;

  const classes = useStyles();

  const [confirmDialogIsOpen, setConfirmDialogIsOpen] = useState(false);

  const isLastStep = useCallback(() => {
    return !!(
      activeListOfSteps.length > 0 &&
      activeListOfSteps.length === activeStep + 1
    );
  }, [activeStep, activeListOfSteps]);

  const getSubmitButtonInfo = useCallback(
    currentStep => {
      if (!isEmpty(activeListOfSteps)) {
        if (isEmpty(resourceCreated) && currentStep === 0) {
          return isLastStep() ? 'Create & Close' : 'Create & Continue';
        }

        if (!isEmpty(resourceCreated) && currentStep === 0) {
          return isFirstStepPristine ? 'Next' : 'Edit & Continue';
        }

        return isLastStep() ? 'Save & Close' : 'Next';
      }
    },
    [resourceCreated, activeListOfSteps, isLastStep, isFirstStepPristine]
  );

  const displayBackButton = useCallback(() => {
    return !!(
      activeListOfSteps.length > 1 &&
      activeStep > 0 &&
      !shouldHideBackButton
    );
  }, [activeListOfSteps, activeStep, shouldHideBackButton]);

  const handleCloseClick = () => setConfirmDialogIsOpen(true);
  const handleConfirmDialogClose = () => setConfirmDialogIsOpen(false);

  return (
    <Dialog
      open={true}
      aria-label={`${resource} Create`}
      maxWidth={false}
      onClose={(_event, reason) => {
        if (reason === 'backdropClick') return null;
        if (reason === 'escapeKeyDown') return handleCloseClick();
      }}
      classes={{
        paperWidthFalse: classes.paperWidthFalse // class name, e.g. `classes-nesting-root-x`
      }}
    >
      <DialogTitleWithCloseButton onClose={handleCloseClick}>
        {!isEmpty(resourceCreated) && shouldDisplayResourceCreatedInfo
          ? displayResourceCreatedInfo()
          : `Create New ${resource}`}
      </DialogTitleWithCloseButton>
      <div className={classes.stepperContainer}>
        <Stepper activeStep={activeStep} className={classes.stepper}>
          {activeListOfSteps.map(step => (
            <Step key={step.name}>
              <StepLabel>{step.label}</StepLabel>
            </Step>
          ))}
        </Stepper>
      </div>
      <DialogContent className={classes.dialogContent}>
        <div className={classes.stepContainer}>
          <Grid container spacing={2}>
            <Grid
              item
              xs={12}
              sm={12}
              md={12}
              lg={8}
              className={
                getDescriptionContainerStyle
                  ? getDescriptionContainerStyle(activeStep, activeListOfSteps)
                  : classes.descriptionContainer_default
              }
            >
              <Typography variant="h5" className={classes.header}>
                {activeListOfSteps[activeStep].label}
              </Typography>
              {hasRadioButtons ? (
                displayRadiosButtons()
              ) : (
                <span className={classes.description}>
                  {activeListOfSteps[activeStep].description}
                </span>
              )}
            </Grid>
          </Grid>
          <customContext.Provider value={providerFormContext}>
            {getActiveStep()}
          </customContext.Provider>
        </div>
        <Confirm
          isOpen={confirmDialogIsOpen}
          title="Close Window?"
          content={generateMessageOnLeave}
          onConfirm={handleClose}
          onClose={handleConfirmDialogClose}
          confirm="ra.action.close"
        />
      </DialogContent>
      <DialogActions
        style={{
          borderTop: `1px solid rgba(0, 0, 0, 0.12)`,
          justifyContent: 'space-between'
        }}
      >
        {displayBackButton() && (
          <div className={classes.leftWrapper}>
            <Button
              onClick={handleBackForm}
              variant="outlined"
              color="primary"
              className={classes.backButton}
              disabled={isBackDisabled}
            >
              Back
            </Button>
          </div>
        )}
        {activeStep === 0 && (
          <Button
            onClick={handleClose}
            color="primary"
            variant="outlined"
            className={classes.button}
          >
            Cancel
          </Button>
        )}
        {isSaveAndCloseStep && (
          <div className={classes.saveAndCloseContainer}>
            <Button
              aria-label="save"
              onClick={handleNextForm}
              color="primary"
              variant="outlined"
              className={classes.paddingBlockButton}
              startIcon={
                isSpinnerOnSaveButton ? (
                  <CircularProgress data-testid="saveSpinner" size={14} />
                ) : (
                  <></>
                )
              }
              disabled={
                !isNextAvailable ||
                isSpinnerOnSaveButton ||
                isSpinnerOnNextButton
              }
              buttonkey={'saveAndClose'}
              data-testid="saveButton"
            >
              Save & close
            </Button>
            <Button
              aria-label="next"
              onClick={handleNextForm}
              color="primary"
              variant="contained"
              className={`${classes.button} ${classes.primaryButton}`}
              startIcon={
                isSpinnerOnNextButton ? (
                  <CircularProgress data-testid="nextSpinner" size={14} />
                ) : (
                  <></>
                )
              }
              disabled={
                !isNextAvailable ||
                isSpinnerOnSaveButton ||
                isSpinnerOnNextButton
              }
              buttonkey={'next'}
              data-testid="nextButton"
            >
              Next
            </Button>
          </div>
        )}
        {!isSaveAndCloseStep && (
          <Button
            data-testid="submit-button"
            aria-label="submit"
            onClick={handleNextForm}
            startIcon={
              isSpinnerOnNextButton ? <CircularProgress size={14} /> : <></>
            }
            color="primary"
            variant="contained"
            disabled={!isNextAvailable || isSpinnerOnNextButton}
            buttonkey={isLastStep() ? 'saveAndClose' : 'next'}
            className={
              isLastStep() || shouldHideBackButton
                ? classes.paddingBlockButton
                : `${classes.button} ${classes.primaryButton}`
            }
          >
            {getSubmitButtonInfo(activeStep)}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default WizardStepper;
